import { IPromise } from 'q';
import { SagaIterator } from 'redux-saga';
import { call, put, takeLatest } from 'redux-saga/effects';

import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import WebHttpClient from '~src/httpClient/WebHttpClient';
import { SiteStore } from '~src/siteManagement/sagas/SiteStore';

import { SiteCountAndCloudAdminStatusAction, SiteNamesRequest, SitesAndTenantsInfoRequest } from '../../bootstrap/redux/actions/AppActionTypes';
import { requestFailed } from '../../bootstrap/redux/actions/RequestFailedAction';
import { Messages } from '../../codegen/Localize';
import { siteCountAndCloudAdminStatusSucceeded } from '../actions/SiteCountAndCloudAdminStatusAction';
import { SitesAndTenantsInfoRequestAction, sitesAndTenantsInfoRequestSucceeded } from '../actions/SitesAndTenantsInfoRequestAction';
import { SiteNamesRequestAction, siteNamesRequestSucceeded } from '../actions/SitesNamesRequestAction';
import { GetSitesAndTenantsAcrossAllPodsRequest } from '../serverApi/requests/GetSitesAndTenantsAcrossAllPodsRequest';
import { ISiteCountAndCloudAdminStatus } from '../serverApi/types/ISiteCountAndCloudAdminStatus';
import { ISiteNameSansId } from '../serverApi/types/ISiteNameSansId';
import { ISitesAndTenantsInfo } from '../serverApi/types/ISitesAndTenantsInfo';
import { SiteNameSansIds } from '../services/resources/SiteNameSansIds';

export const MAX_IN_CLAUSE_VALUES: number = 1000;

export function* handleGetSitesRequest(action: SiteNamesRequestAction) {
  try {
    const sites: ReadonlyArray<ISiteNameSansId> = yield call(getSiteNamesRequest);
    if (sites.length < 1) {
      yield put(requestFailed(Messages.errorUnknown()));
      return;
    }
    yield put(siteNamesRequestSucceeded(sites));
  } catch(e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function getSiteNamesRequest(): IPromise<ISiteNameSansId[]> {
  const siteStore = new SiteStore(SiteNameSansIds.instance);
  return siteStore.getAll();
}

export function* handleGetSiteCountAndCloudAdminStatus(action: SitesAndTenantsInfoRequestAction) {
  try {
    let siteCountAndCloudAdminStatus: ISiteCountAndCloudAdminStatus;
    if(FeatureFlags.syncToLinkEnabled) {
      const sitesAndTenantsInfo: ISitesAndTenantsInfo = yield call(getSitesAndTenantsInfoRequest);
      siteCountAndCloudAdminStatus = {
        numberOfSites: sitesAndTenantsInfo.sitesInfo?.length,
        isCloudAdmin: sitesAndTenantsInfo.managedTenants?.length > 0
      };
    } else {
      const sites: ReadonlyArray<ISiteNameSansId> = yield call(getSiteNamesRequest);
      siteCountAndCloudAdminStatus = {
        numberOfSites: sites.length,
        isCloudAdmin: false
      };
    }
    yield put(siteCountAndCloudAdminStatusSucceeded(siteCountAndCloudAdminStatus));
  } catch(e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function* handleGetSitesAndTenantsInfoRequest(action: SitesAndTenantsInfoRequestAction) {
  try {
    const sitesAndTenantsInfo: ISitesAndTenantsInfo = yield call(getSitesAndTenantsInfoRequest);
    yield put(sitesAndTenantsInfoRequestSucceeded(sitesAndTenantsInfo));
  } catch(e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function getSitesAndTenantsInfoRequest(): Promise<ISitesAndTenantsInfo> {
  return WebHttpClient.sendRequest(new GetSitesAndTenantsAcrossAllPodsRequest())
    .then((sitesAndTenantsInfo: ISitesAndTenantsInfo) => {
      if (sitesAndTenantsInfo.errors) {
        return Promise.reject(sitesAndTenantsInfo.errors[0] && sitesAndTenantsInfo.errors[0].code);
      }
      return Promise.resolve(sitesAndTenantsInfo);
  });
}

export function* rootSiteSagas(): SagaIterator {
  yield takeLatest(SiteNamesRequest, handleGetSitesRequest);
  yield takeLatest(SitesAndTenantsInfoRequest, handleGetSitesAndTenantsInfoRequest);
  yield takeLatest(SiteCountAndCloudAdminStatusAction, handleGetSiteCountAndCloudAdminStatus);
}

